import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, DollarSign, IndianRupee, Download, FileText, FileSpreadsheet, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import SearchableCandidateSelect from '@/components/SearchableCandidateSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Closures() {
  const [closures, setClosures] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [open, setOpen] = useState(false);
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [revenueAmount, setRevenueAmount] = useState('');
  const [offeredCtc, setOfferedCtc] = useState('');
  const [user, setUser] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({ resume_id: '', remarks: '' });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const fetchClosures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/closures`, { headers: { Authorization: `Bearer ${token}` } });
      setClosures(response.data);
    } catch (error) { toast.error('Failed to fetch closures'); }
  };

  const fetchResumes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/resumes`, { headers: { Authorization: `Bearer ${token}` } });
      setResumes(response.data);
    } catch (error) { toast.error('Failed to fetch resumes'); }
  };

  useEffect(() => { fetchClosures(); fetchResumes(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/closures`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Closure recorded successfully');
      setOpen(false); setFormData({ resume_id: '', remarks: '' }); fetchClosures();
    } catch (error) { toast.error('Failed to record closure'); }
  };

  const handleUpdateRevenue = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/closures/${selectedClosure.id}/revenue`, {
        revenue: parseFloat(revenueAmount), offered_ctc: offeredCtc ? parseFloat(offeredCtc) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Revenue and CTC updated'); setRevenueDialogOpen(false);
      setSelectedClosure(null); setRevenueAmount(''); setOfferedCtc(''); fetchClosures();
    } catch (error) { toast.error('Failed to update revenue'); }
  };

  const isAdmin = user && user.role === 'admin';
  const canSeeRevenue = user && user.role === 'admin';
  const canExport = user && (user.role === 'admin' || user.role === 'team_leader');

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this closure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/closures/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Closure deleted'); fetchClosures();
    } catch (error) { toast.error('Failed to delete closure'); }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/closures?format=${format}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `closures.${ext}`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) { toast.error('Failed to export closures'); }
  };

  const uniqueCompanies = useMemo(() => [...new Set(closures.map(c => c.client_name).filter(Boolean))].sort(), [closures]);
  const uniqueRecruiters = useMemo(() => [...new Set(closures.map(c => c.recruiter_name).filter(Boolean))].sort(), [closures]);

  const filteredClosures = useMemo(() => {
    let data = [...closures];
    const q = search.trim().toLowerCase();
    if (q) {
      data = data.filter(c =>
        (c.candidate_name || '').toLowerCase().includes(q) ||
        (c.client_name || '').toLowerCase().includes(q) ||
        (c.position_title || '').toLowerCase().includes(q) ||
        (c.recruiter_name || '').toLowerCase().includes(q)
      );
    }
    if (filterCompany !== 'all') data = data.filter(c => c.client_name === filterCompany);
    if (filterRecruiter !== 'all') data = data.filter(c => c.recruiter_name === filterRecruiter);
    if (filterDateFrom) data = data.filter(c => (c.created_at || '') >= filterDateFrom);
    if (filterDateTo) data = data.filter(c => (c.created_at || '').slice(0, 10) <= filterDateTo);
    return data;
  }, [closures, filterCompany, filterRecruiter, filterDateFrom, filterDateTo, search]);

  const clearFilters = () => { setFilterCompany('all'); setFilterRecruiter('all'); setFilterDateFrom(''); setFilterDateTo(''); setSearch(''); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Closures</h1>
          <p className="text-slate-600">Track successful placements</p>
        </div>
        <div className="flex items-center gap-3">
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-closures-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50"><Download size={16} className="mr-2" />Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-closures-excel" onClick={() => handleExport('excel')}><FileSpreadsheet size={16} className="mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-closures-pdf" onClick={() => handleExport('pdf')}><FileText size={16} className="mr-2" />PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-closure-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Add Closure</Button>
            </DialogTrigger>
            <DialogContent className="rounded">
              <DialogHeader><DialogTitle className="text-2xl font-bold text-blue-900">Record Closure</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Candidate</Label>
                  <select data-testid="closure-resume-select" value={formData.resume_id} onChange={(e) => setFormData({ ...formData, resume_id: e.target.value })} required className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                    <option value="">Select a candidate</option>
                    {resumes.map(r => <option key={r.id} value={r.id}>{r.candidate_name} - {r.position_title}</option>)}
                  </select>
                  <div className="mt-2">
                    <SearchableCandidateSelect
                      value={formData.resume_id}
                      onChange={(id) => setFormData({ ...formData, resume_id: id })}
                      resumes={resumes}
                      testId="closure-resume"
                    />
                  </div></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Remarks</Label>
                  <textarea data-testid="closure-remarks-input" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Optional remarks..." /></div>
                <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded border border-blue-200"><strong>Note:</strong> Revenue and offered CTC will be added by admin later</p>
                <Button type="submit" data-testid="submit-closure-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Record Closure</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search</Label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="filter-closure-search" type="search" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Candidate, company, position, recruiter..."
              className="pl-8 h-9 rounded border-blue-200 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Company</Label>
          <select data-testid="filter-closure-company" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Companies</option>
            {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
          <select data-testid="filter-closure-recruiter" value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Recruiters</option>
            {uniqueRecruiters.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From Date</Label>
          <Input data-testid="filter-closure-from" type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To Date</Label>
          <Input data-testid="filter-closure-to" type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 text-xs">Clear Filters</Button>
        <span className="text-xs text-slate-500 ml-auto">{filteredClosures.length} of {closures.length} closures</span>
      </div>

      {/* Revenue Dialog */}
      <Dialog open={revenueDialogOpen} onOpenChange={setRevenueDialogOpen}>
        <DialogContent className="rounded">
          <DialogHeader><DialogTitle className="text-2xl font-bold text-blue-900">Update Revenue & CTC</DialogTitle></DialogHeader>
          {selectedClosure && (
            <form onSubmit={handleUpdateRevenue} className="space-y-4 mt-4">
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <p className="text-sm text-slate-600 mb-1">Candidate: <strong className="text-blue-900">{selectedClosure.candidate_name}</strong></p>
                <p className="text-sm text-slate-600">Position: <strong className="text-blue-900">{selectedClosure.position_title}</strong></p>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Revenue</Label>
                <div className="relative mt-2"><IndianRupee size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="revenue-input" type="number" step="0.01" value={revenueAmount} onChange={(e) => setRevenueAmount(e.target.value)} required className="pl-10 rounded border-blue-200" placeholder="Enter revenue in INR" /></div></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Offered CTC (/year)</Label>
                <div className="relative mt-2"><IndianRupee size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="offered-ctc-input" type="number" step="0.01" value={offeredCtc} onChange={(e) => setOfferedCtc(e.target.value)} className="pl-10 rounded border-blue-200" placeholder="Enter offered CTC" /></div></div>
              <Button type="submit" data-testid="submit-revenue-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Update Revenue & CTC</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900">Candidate</TableHead>
              <TableHead className="font-bold text-blue-900">Position</TableHead>
              <TableHead className="font-bold text-blue-900">Client</TableHead>
              {canSeeRevenue && <TableHead className="font-bold text-blue-900">Revenue</TableHead>}
              {canSeeRevenue && <TableHead className="font-bold text-blue-900">Offered CTC</TableHead>}
              <TableHead className="font-bold text-blue-900">Recruiter</TableHead>
              <TableHead className="font-bold text-blue-900">Remarks</TableHead>
              <TableHead className="font-bold text-blue-900">Date</TableHead>
              {isAdmin && <TableHead className="font-bold text-blue-900">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClosures.length === 0 ? (
              <TableRow><TableCell colSpan={canSeeRevenue ? (isAdmin ? 9 : 8) : (isAdmin ? 7 : 6)} className="text-center text-slate-500 py-8">
                <DollarSign size={48} className="mx-auto mb-2 text-slate-300" />No closures found</TableCell></TableRow>
            ) : (
              filteredClosures.map((closure) => (
                <TableRow key={closure.id} data-testid={`closure-row-${closure.id}`} className="hover:bg-blue-50">
                  <TableCell className="font-semibold text-blue-900">{closure.candidate_name}</TableCell>
                  <TableCell>{closure.position_title}</TableCell>
                  <TableCell className="text-sm">{closure.client_name || <span className="text-slate-400">-</span>}</TableCell>
                  {canSeeRevenue && <TableCell className="font-bold text-teal-700">{closure.revenue ? `₹${closure.revenue.toLocaleString('en-IN')}` : <span className="text-slate-400">Not set</span>}</TableCell>}
                  {canSeeRevenue && <TableCell className="font-semibold text-blue-700">{closure.offered_ctc ? `₹${closure.offered_ctc.toLocaleString('en-IN')}/yr` : <span className="text-slate-400">Not set</span>}</TableCell>}
                  <TableCell>{closure.recruiter_name}</TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[150px] truncate">{closure.remarks || <span className="text-slate-300">-</span>}</TableCell>
                  <TableCell className="text-slate-500">{new Date(closure.created_at).toLocaleDateString()}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedClosure(closure); setRevenueAmount(closure.revenue || ''); setOfferedCtc(closure.offered_ctc || ''); setRevenueDialogOpen(true); }} className="rounded border-blue-300 text-blue-700 hover:bg-blue-50">
                          <DollarSign size={14} className="mr-1" />{closure.revenue ? 'Update' : 'Add'}
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-closure-${closure.id}`} onClick={() => handleDelete(closure.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50"><Trash2 size={14} /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
